import React from "react";
import OrderAddressCard from "./OrderAddressCard";
import CustomerContactCard from "./CustomerContactCard";
import OrderStatusTimeline from "./OrderStatusTimeline";

interface Props {
  order: any;
  isRTL: boolean;
  t: (key: string, options?: any) => string;
  formatPhoneForWhatsApp: (phone: string) => string;
}

const OrderDetailsRightColumn: React.FC<Props> = ({
  order,
  isRTL,
  t,
  formatPhoneForWhatsApp,
}) => {
  const [showContactOptions, setShowContactOptions] = React.useState(false);

  return (
    <div className="space-y-6">
      {/* Customer Info Card */}
      <div className="bg-white rounded-2xl shadow-lg p-6">
        <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <span className="text-purple-500">👤</span>
          {t("order.customerInformation")}
        </h3>
        <div className="space-y-4">
          <div className={`flex items-center gap-3 ${isRTL ? "flex-row-reverse" : ""}`}>
            <div className="w-12 h-12 bg-gradient-to-br from-purple-100 to-pink-100 rounded-full flex items-center justify-center">
              <span className="text-xl">👤</span>
            </div>
            <div className={isRTL ? "text-right" : ""}>
              <div className="font-semibold text-gray-800">{order.customer.username}</div>
              <div className="text-sm text-gray-600">
                {t("order.customerId")}: {order.customer.id}
              </div>
            </div>
          </div>

          <div className="space-y-3 pt-4 border-t border-gray-100">
            <div className={`flex justify-between ${isRTL ? "flex-row-reverse" : ""}`}>
              <span className="text-gray-600">{t("order.orderDate")}</span>
              <span className="font-medium">{new Date(order.created_at).toLocaleDateString()}</span>
            </div>
            <div className={`flex justify-between ${isRTL ? "flex-row-reverse" : ""}`}>
              <span className="text-gray-600">{t("order.lastUpdated")}</span>
              <span className="font-medium">{new Date(order.updated_at).toLocaleDateString()}</span>
            </div>
            <div className={`flex justify-between ${isRTL ? "flex-row-reverse" : ""}`}>
              <span className="text-gray-600">{t("order.totalItems")}</span>
              <span className="font-medium">{order.items.length}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Shipping Address Card */}
      {order?.address && (
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <OrderAddressCard address={order.address} isRTL={isRTL} t={t} />
        </div>
      )}

      {/* Quick Actions */}
      {order.address?.mobile && (
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <span className="text-green-500">⚡</span>
            {t("order.quickActions")}
          </h3>
          <div className="space-y-3">
            <a
              href={`tel:${order.address.mobile}`}
              className="flex items-center justify-center gap-2 px-4 py-3 bg-green-500 hover:bg-green-600 text-white rounded-xl transition-colors duration-200 font-medium"
            >
              <span>📞</span>
              {t("order.callNow")}
            </a>
            <a
              href={`https://wa.me/${formatPhoneForWhatsApp(order.address.mobile)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 px-4 py-3 bg-green-600 hover:bg-green-700 text-white rounded-xl transition-colors duration-200 font-medium"
            >
              <span>💬</span>
              {t("order.whatsappNow")}
            </a>
          </div>
        </div>
      )}

      {/* Order Timeline */}
      <div className="bg-white rounded-2xl shadow-lg p-6">
        <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <span className="text-orange-500">⏱️</span>
          {t("order.timeline")}
        </h3>
        <div className="space-y-4">
          <div className={`flex items-start gap-3 ${isRTL ? "flex-row-reverse" : ""}`}>
            <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-green-600">✓</span>
            </div>
            <div className={isRTL ? "text-right" : ""}>
              <div className="font-medium text-gray-800">{t("order.orderCreated")}</div>
              <div className="text-sm text-gray-500">{new Date(order.created_at).toLocaleString()}</div>
            </div>
          </div>
          <div className={`flex items-start gap-3 ${isRTL ? "flex-row-reverse" : ""}`}>
            <div className={`w-8 h-8 ${order.status !== "pending" ? "bg-blue-100" : "bg-gray-100"} rounded-full flex items-center justify-center flex-shrink-0`}>
              <span className={order.status !== "pending" ? "text-blue-600" : "text-gray-400"}>
                {order.status !== "pending" ? "✓" : "•"}
              </span>
            </div>
            <div className={isRTL ? "text-right" : ""}>
              <div className="font-medium text-gray-800">{t("order.orderConfirmed")}</div>
              <div className="text-sm text-gray-500">
                {order.status !== "pending" ? new Date(order.updated_at).toLocaleString() : t("order.pending")}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderDetailsRightColumn;
